// Hook para tracking de sessão e tempo gasto
import { useEffect, useRef, useCallback } from 'react';

export const useSessionTracking = (plausible) => {
  const sectionStartTime = useRef(null);
  const currentSection = useRef(null);
  const sessionStartTime = useRef(null);
  const isPageVisible = useRef(true);

  // Inicializar sessão
  useEffect(() => {
    if (!sessionStartTime.current) {
      sessionStartTime.current = Date.now();
      window.sessionStartTime = sessionStartTime.current;
    }

    // Track page visibility changes
    const handleVisibilityChange = () => {
      isPageVisible.current = !document.hidden;
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Função para iniciar tracking de uma seção
  const startSection = useCallback((sectionName) => {
    // Se há uma seção anterior, finalizar ela
    if (currentSection.current && sectionStartTime.current && plausible) {
      const timeSpent = (Date.now() - sectionStartTime.current) / 1000;
      if (isPageVisible.current && timeSpent > 1) { // Só trackear se visível e > 1s
        plausible.trackSectionTime(currentSection.current, timeSpent);
      }
    }

    // Iniciar nova seção
    currentSection.current = sectionName;
    sectionStartTime.current = Date.now();
    
    if (plausible) {
      plausible.trackSection(sectionName);
    }
  }, [plausible]);

  // Função para finalizar seção com sucesso
  const completeSection = useCallback((sectionName = null) => {
    const section = sectionName || currentSection.current;
    if (section && sectionStartTime.current && plausible) {
      const timeSpent = (Date.now() - sectionStartTime.current) / 1000;
      if (isPageVisible.current && timeSpent > 1) {
        plausible.trackSectionTime(section, timeSpent);
        plausible.trackSection('section-complete');
      }
    }
  }, [plausible]);

  // Função para trackear abandono
  const abandonSection = useCallback((reason = 'manual', progressPercentage = 0) => {
    if (currentSection.current && plausible) {
      const timeSpent = sectionStartTime.current ? 
        (Date.now() - sectionStartTime.current) / 1000 : 0;
      
      plausible.trackAbandon(currentSection.current, reason, progressPercentage);
      plausible.trackSection('section-abandon');
    }
  }, [plausible]);

  // Função para calcular progresso na seção atual
  const getSessionDuration = useCallback(() => {
    return sessionStartTime.current ? 
      (Date.now() - sessionStartTime.current) / 1000 : 0;
  }, []);

  const getSectionDuration = useCallback(() => {
    return sectionStartTime.current ? 
      (Date.now() - sectionStartTime.current) / 1000 : 0;
  }, []);

  return {
    startSection,
    completeSection,
    abandonSection,
    getSessionDuration,
    getSectionDuration,
    currentSection: currentSection.current
  };
};
